/**
 * Per-state county counts for the public coverage page.
 *
 * These are the same figures already published on the four location list pages, so the
 * coverage hub cannot drift from them. Counts include county equivalents: Louisiana
 * parishes, Alaska boroughs and census areas, and Virginia independent cities.
 *
 * Rule: this file describes the nationwide assessor and pre-foreclosure layer, which is
 * the coverage the "3,000+ counties" claim rests on. Probate and successor availability
 * is driven by what each county clerk publishes and is deliberately NOT expressed as a
 * per-county number here. Do not add one without verified data behind it.
 */

import { FaqItem } from "../models/home/faq-item";
import { CountiesLabel, StatesLabel, WeeklyCadenceReason } from "./data-facts.consts";

export interface CoverageState {
  readonly name: string;
  readonly code: string;
  readonly slug: string;
  readonly counties: number;
  /** Set where the state does not call its subdivisions "counties". */
  readonly countyNoun?: string;
}

export const CoverageStates: readonly CoverageState[] = [
  { name: "Alabama", code: "AL", slug: "alabama", counties: 67 },
  { name: "Alaska", code: "AK", slug: "alaska", counties: 29, countyNoun: "boroughs and census areas" },
  { name: "Arizona", code: "AZ", slug: "arizona", counties: 15 },
  { name: "Arkansas", code: "AR", slug: "arkansas", counties: 75 },
  { name: "California", code: "CA", slug: "california", counties: 58 },
  { name: "Colorado", code: "CO", slug: "colorado", counties: 64 },
  { name: "Connecticut", code: "CT", slug: "connecticut", counties: 8 },
  { name: "Delaware", code: "DE", slug: "delaware", counties: 3 },
  { name: "Florida", code: "FL", slug: "florida", counties: 67 },
  { name: "Georgia", code: "GA", slug: "georgia", counties: 159 },
  { name: "Hawaii", code: "HI", slug: "hawaii", counties: 5 },
  { name: "Idaho", code: "ID", slug: "idaho", counties: 44 },
  { name: "Illinois", code: "IL", slug: "illinois", counties: 102 },
  { name: "Indiana", code: "IN", slug: "indiana", counties: 92 },
  { name: "Iowa", code: "IA", slug: "iowa", counties: 99 },
  { name: "Kansas", code: "KS", slug: "kansas", counties: 105 },
  { name: "Kentucky", code: "KY", slug: "kentucky", counties: 120 },
  { name: "Louisiana", code: "LA", slug: "louisiana", counties: 64, countyNoun: "parishes" },
  { name: "Maine", code: "ME", slug: "maine", counties: 16 },
  { name: "Maryland", code: "MD", slug: "maryland", counties: 24 },
  { name: "Massachusetts", code: "MA", slug: "massachusetts", counties: 14 },
  { name: "Michigan", code: "MI", slug: "michigan", counties: 83 },
  { name: "Minnesota", code: "MN", slug: "minnesota", counties: 87 },
  { name: "Mississippi", code: "MS", slug: "mississippi", counties: 82 },
  { name: "Missouri", code: "MO", slug: "missouri", counties: 115 },
  { name: "Montana", code: "MT", slug: "montana", counties: 56 },
  { name: "Nebraska", code: "NE", slug: "nebraska", counties: 93 },
  { name: "Nevada", code: "NV", slug: "nevada", counties: 17 },
  { name: "New Hampshire", code: "NH", slug: "new-hampshire", counties: 10 },
  { name: "New Jersey", code: "NJ", slug: "new-jersey", counties: 21 },
  { name: "New Mexico", code: "NM", slug: "new-mexico", counties: 33 },
  { name: "New York", code: "NY", slug: "new-york", counties: 62 },
  { name: "North Carolina", code: "NC", slug: "north-carolina", counties: 100 },
  { name: "North Dakota", code: "ND", slug: "north-dakota", counties: 53 },
  { name: "Ohio", code: "OH", slug: "ohio", counties: 88 },
  { name: "Oklahoma", code: "OK", slug: "oklahoma", counties: 77 },
  { name: "Oregon", code: "OR", slug: "oregon", counties: 36 },
  { name: "Pennsylvania", code: "PA", slug: "pennsylvania", counties: 67 },
  { name: "Rhode Island", code: "RI", slug: "rhode-island", counties: 5 },
  { name: "South Carolina", code: "SC", slug: "south-carolina", counties: 46 },
  { name: "South Dakota", code: "SD", slug: "south-dakota", counties: 66 },
  { name: "Tennessee", code: "TN", slug: "tennessee", counties: 95 },
  { name: "Texas", code: "TX", slug: "texas", counties: 254 },
  { name: "Utah", code: "UT", slug: "utah", counties: 29 },
  { name: "Vermont", code: "VT", slug: "vermont", counties: 14 },
  { name: "Virginia", code: "VA", slug: "virginia", counties: 133, countyNoun: "counties and independent cities" },
  { name: "Washington", code: "WA", slug: "washington", counties: 39 },
  { name: "West Virginia", code: "WV", slug: "west-virginia", counties: 55 },
  { name: "Wisconsin", code: "WI", slug: "wisconsin", counties: 72 },
  { name: "Wyoming", code: "WY", slug: "wyoming", counties: 23 }
];

/** The four data sets a state row links out to. */
export const CoverageVerticals = [
  { label: "Property records", basePath: "/assessors-data/locations" },
  { label: "Pre-foreclosure", basePath: "/foreclosure-data/locations" },
  { label: "Probate", basePath: "/probate-leads/locations" },
  { label: "Successors", basePath: "/successors-data/locations" }
] as const;

/**
 * Shared by the page template and by the FAQPage schema in the resolver, so the two can
 * never drift. Not readonly: the shared <app-faq> component takes a mutable FaqItem[].
 */
export const CoverageFaqs: FaqItem[] = [
  {
    question: "Which states do you cover?",
    answer: `Assessor property records and pre-foreclosure filings cover ${StatesLabel} and ${CountiesLabel} counties and county equivalents, including Louisiana parishes, Alaska boroughs and Virginia independent cities.`
  },
  {
    question: "Is probate and successor data available in every county?",
    answer:
      "No, and we will not claim otherwise. Probate cases and inherited property transfers come from county clerks and recorders, and what is published varies by county, by court and sometimes by month. Our nationwide property and pre-foreclosure layer is the consistent one. For probate and successors, check the state page for the county you work before you subscribe."
  },
  {
    question: "How often is each data set refreshed?",
    answer: `Assessor records and pre-foreclosure filings are updated daily. Successor and probate records are updated weekly, every Tuesday. ${WeeklyCadenceReason}`
  },
  {
    question: "Do you charge more for larger states?",
    answer:
      "No. Pricing is per data set, not per state or per county. Assessors Data is $60 per month and includes Pre-Foreclosure, Successors Data and Probate Leads are $125 per month each, and the bundle covering everything is $250 per month."
  }
];
